import dayjs from 'dayjs'
import columns from 'UI/VerticalTable/columns'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { ISubmitParams } from './Filters/Filters'
import { ElasticRange } from 'utils/types'
import { ITableColumnsType } from 'uikit/tables/default'

export const activityColumns: ITableColumnsType<any> = [
  columns[COLUMNS_IDS.cryptoTokenName],
  columns[COLUMNS_IDS.cryptoActivityType],
  columns[COLUMNS_IDS.cryptoDateAndTime],
  columns[COLUMNS_IDS.cryptoTransactionHash],
  columns[COLUMNS_IDS.cryptoAmount],
  columns[COLUMNS_IDS.cryptoGainLossUSD],
  columns[COLUMNS_IDS.cryptoActivityFrom],
  columns[COLUMNS_IDS.cryptoFromLabel],
  columns[COLUMNS_IDS.cryptoActivityTo],
  columns[COLUMNS_IDS.cryptoToLabel],
  columns[COLUMNS_IDS.cryptoDirection],
  columns[COLUMNS_IDS.cryptoCostBasisUSD],
  columns[COLUMNS_IDS.cryptoSellValueUSD],
  columns[COLUMNS_IDS.cryptoTokenType],
  columns[COLUMNS_IDS.cryptoChain],
]

export const activitySubColumns: ITableColumnsType<any> = [
  columns[COLUMNS_IDS.cryptoCostBasisItem],
  columns[COLUMNS_IDS.cryptoActivityType],
  columns[COLUMNS_IDS.cryptoCostBasisDateAndTime],
  columns[COLUMNS_IDS.cryptoTransactionHash],
  columns[COLUMNS_IDS.cryptoAmount],
  columns[COLUMNS_IDS.costBasisAllocation],
  columns[COLUMNS_IDS.cryptoActivityFrom],
  columns[COLUMNS_IDS.cryptoActivityTo],
  columns[COLUMNS_IDS.cryptoCostBasisUSD],
  columns[COLUMNS_IDS.cryptoPurchaseValue],
]

// Generate dynamic sub headers based on currency context
export const getActivityExportSubHeaders = (currencyContext: any) => {
  const isCurrencyConversion = currencyContext?.currencyConversion && currencyContext?.currentCurrency !== 'USD'
  const currency = currencyContext?.currentCurrency || 'USD'

  const baseHeaders = [
    {
      label: 'Token ID',
      value: 'tokenId',
      placement: 'tokenId',
    },
    {
      label: 'Item',
      value: 'tokenName',
      placement: 'tokenName',
    },
    {
      label: 'Activity Type',
      value: 'activityType',
      placement: 'activityType',
    },
    {
      label: 'Date and Time',
      value: 'activityDate',
      placement: 'activityDate',
    },
    {
      label: 'Transaction Hash',
      value: 'transactionHash',
      placement: 'transactionHash',
    },
    {
      label: 'Amount',
      value: 'amount',
      placement: 'amount',
    },
    {
      label: 'Cost Basis Allocation',
      value: 'allocated',
      placement: 'revenue',
    },
    {
      label: 'From',
      value: 'activityFrom',
      placement: 'activityFrom',
    },
    {
      label: 'To',
      value: 'activityTo',
      placement: 'activityTo',
    },
    {
      label: 'Cost Basis (USD)',
      value: 'costBasis',
      placement: 'costBasis',
    },
  ]

  if (isCurrencyConversion) {
    baseHeaders.push({
      label: `Cost Basis (${currency})`,
      value: 'costBasisConverted',
      placement: 'costBasisConverted',
    })
  }

  baseHeaders.push({
    label: 'Sell Value (USD)',
    value: 'value',
    placement: 'value',
  })

  if (isCurrencyConversion) {
    baseHeaders.push({
      label: `Sell Value (${currency})`,
      value: 'sellValueConverted',
      placement: 'sellValueConverted',
    })
  }

  // Add single conversion rate at the end when currency conversion is enabled
  if (isCurrencyConversion) {
    baseHeaders.push({
      label: 'Conversion Rate',
      value: 'conversionRate',
      placement: 'conversionRate',
    })
  }

  return baseHeaders
}
export const getRangePickerStringValues = (dateValue?: unknown) => {
  if (Array.isArray(dateValue) && dateValue.length === 2) {
    const [dateValueFirst, dateValueSecond] = dateValue

    if (dateValueFirst && dateValueSecond && dayjs.isDayjs(dateValueFirst) && dayjs.isDayjs(dateValueSecond)) {
      const start = dayjs(dateValueFirst).startOf('month').format('YYYY/MM/DD 00:00')
      const end = dayjs(dateValueSecond).endOf('month').format('YYYY/MM/DD 23:59')

      return {
        start,
        end,
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

type FilterField = 'activityFrom' | 'activityType' | 'direction' | 'tokenType' | 'gainLossValue'

type ElasticFilter = {
  [key in FilterField]?: string
}

type ElasticQueryParts = {
  filters: ElasticFilter[]
  range?: ElasticRange
}

export const generateElasticFilters = (input: ISubmitParams): ElasticQueryParts => {
  const mapping: Record<keyof ISubmitParams, FilterField | null> = {
    activitiesFrom: 'activityFrom',
    activityTypes: 'activityType',
    directions: 'direction',
    tokenTypes: 'tokenType',
    gainLossType: null,
    gainLossValue: 'gainLossValue',
  }

  const filterArrays: ElasticFilter[][] = []

  for (const [key, mappedKey] of Object.entries(mapping)) {
    if (!mappedKey || key === 'gainLossValue') continue

    const value = input[key as keyof ISubmitParams]
    if (Array.isArray(value) && value.length > 0) {
      filterArrays.push(value.map(v => ({ [mappedKey]: v })))
    } else if (typeof value === 'string' && value.trim() !== '') {
      filterArrays.push([{ [mappedKey]: value }])
    }
  }

  const cartesianProduct = (arrays: ElasticFilter[][]): ElasticFilter[] => {
    return arrays.reduce<ElasticFilter[]>((acc, curr) => acc.flatMap(a => curr.map(b => ({ ...a, ...b }))), [{}])
  }

  const filters = cartesianProduct(filterArrays)

  let range: ElasticQueryParts['range']
  const gainLossValueNum = Number(input.gainLossValue)

  if (input.gainLossValue !== '' && !isNaN(gainLossValueNum) && input.gainLossType) {
    const key = input.gainLossType === 'greaterThan' ? 'gt' : 'lt'
    range = {
      range: {
        revenue: {
          [key]: gainLossValueNum,
        },
      },
    }
  }

  return { filters, range }
}

export const convertToYearMonth = (dateStr: string | null | undefined): string => {
  if (!dateStr) {
    return ''
  }
  const date = new Date(dateStr.replace(/\//g, '-'))
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}
